import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Modal from '../Layout/Modal';
import ElevatedButton from '../Layout/ElevatedButton';
import { API_ENDPOINTS } from '../../api/config';

const NotesContainer = styled.div`
  padding: 1rem;
  max-height: 70vh;
  overflow-y: auto;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.text};
  font-size: 1.1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid ${props => props.theme.border};
  padding-bottom: 0.5rem;
`;

const NotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const NoteItem = styled.div`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 1rem;
  position: relative;
`;

const NoteContent = styled.p`
  color: ${props => props.theme.text};
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const NoteTimestamp = styled.span`
  color: ${props => props.theme.textMuted};
  font-size: 0.8rem;
  margin-top: 0.5rem;
  display: block;
`;

const AddNoteSection = styled.div`
  margin-top: 1rem;
`;

const NoteTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const CourseNotesModal = ({ isOpen, onClose, course }) => {
  const [notes, setNotes] = useState({ uploads: [], writtenNotes: [], quickNotes: [], todos: [] });
  const [newWrittenNote, setNewWrittenNote] = useState('');
  const [newQuickNote, setNewQuickNote] = useState('');
  const [newTodo, setNewTodo] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchNotes = useCallback(async () => {
    if (!course?._id || !isOpen) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.COURSE_BY_ID(course._id)}/notes`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setNotes(data.notes || { uploads: [], writtenNotes: [], quickNotes: [], todos: [] });
      } else {
        console.error('Failed to fetch course notes');
        setNotes({ uploads: [], writtenNotes: [], quickNotes: [] });
      }
    } catch (error) {
      console.error('Error fetching course notes:', error);
      setNotes({ uploads: [], writtenNotes: [], quickNotes: [] });
    }
  }, [course, isOpen]);

  useEffect(() => {
    fetchNotes();
  }, [isOpen, course, fetchNotes]);

  const updateNotes = async (updates) => {
    if (!course?._id) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.COURSE_BY_ID(course._id)}/notes`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        await fetchNotes(); // Refresh notes
      } else {
        console.error('Failed to update course notes');
      }
    } catch (error) {
      console.error('Error updating course notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddWrittenNote = () => {
    if (!newWrittenNote.trim()) return;

    const updatedNotes = {
      writtenNotes: [
        ...notes.writtenNotes,
        {
          content: newWrittenNote.trim(),
          timestamp: new Date()
        }
      ]
    };

    updateNotes(updatedNotes);
    setNewWrittenNote('');
  };

  const handleAddQuickNote = () => {
    if (!newQuickNote.trim()) return;

    const updatedNotes = {
      quickNotes: [
        ...notes.quickNotes,
        {
          content: newQuickNote.trim(),
          timestamp: new Date()
        }
      ]
    };

    updateNotes(updatedNotes);
    setNewQuickNote('');
  };

  const handleDeleteWrittenNote = (index) => {
    const updatedNotes = {
      writtenNotes: notes.writtenNotes.filter((_, i) => i !== index)
    };
    updateNotes(updatedNotes);
  };

  const handleDeleteQuickNote = (index) => {
    const updatedNotes = {
      quickNotes: notes.quickNotes.filter((_, i) => i !== index)
    };
    updateNotes(updatedNotes);
  };

  const handleAddTodo = () => {
    if (!newTodo.trim()) return;

    const updatedNotes = {
      todos: [
        ...notes.todos,
        {
          content: newTodo.trim(),
          status: 'INPROGRESS',
          timestamp: new Date()
        }
      ]
    };

    updateNotes(updatedNotes);
    setNewTodo('');
  };

  const handleDeleteTodo = (index) => {
    const updatedNotes = {
      todos: notes.todos.filter((_, i) => i !== index)
    };
    updateNotes(updatedNotes);
  };

  const handleToggleTodoStatus = (index) => {
    const updatedTodos = notes.todos.map((todo, i) =>
      i === index ? { ...todo, status: todo.status === 'INPROGRESS' ? 'COMPLETED' : 'INPROGRESS' } : todo
    );
    updateNotes({ todos: updatedTodos });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${course?.course_code || 'Course'} - Notes`}
    >
      <NotesContainer>
        {/* Written Notes Section */}
        <Section>
          <SectionTitle>Written Notes</SectionTitle>
          {notes.writtenNotes && notes.writtenNotes.length > 0 ? (
            <NotesList>
              {notes.writtenNotes.map((note, index) => (
                <NoteItem key={index}>
                  <NoteContent>{note.content}</NoteContent>
                  <NoteTimestamp>
                    {new Date(note.timestamp).toLocaleString()}
                    <button
                      onClick={() => handleDeleteWrittenNote(index)}
                      style={{
                        marginLeft: '0.5rem',
                        background: 'none',
                        border: 'none',
                        color: '#dc3545',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      Delete
                    </button>
                  </NoteTimestamp>
                </NoteItem>
              ))}
            </NotesList>
          ) : (
            <p style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
              No written notes yet.
            </p>
          )}

          <AddNoteSection>
            <NoteTextarea
              placeholder="Add a written note..."
              value={newWrittenNote}
              onChange={(e) => setNewWrittenNote(e.target.value)}
            />
            <ButtonRow>
              <ElevatedButton
                onClick={handleAddWrittenNote}
                disabled={!newWrittenNote.trim() || loading}
              >
                Add Written Note
              </ElevatedButton>
            </ButtonRow>
          </AddNoteSection>
        </Section>

        {/* Quick Notes Section */}
        <Section>
          <SectionTitle>Quick Notes</SectionTitle>
          {notes.quickNotes && notes.quickNotes.length > 0 ? (
            <NotesList>
              {notes.quickNotes.map((note, index) => (
                <NoteItem key={index}>
                  <NoteContent>{note.content}</NoteContent>
                  <NoteTimestamp>
                    {new Date(note.timestamp).toLocaleString()}
                    <button
                      onClick={() => handleDeleteQuickNote(index)}
                      style={{
                        marginLeft: '0.5rem',
                        background: 'none',
                        border: 'none',
                        color: '#dc3545',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      Delete
                    </button>
                  </NoteTimestamp>
                </NoteItem>
              ))}
            </NotesList>
          ) : (
            <p style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
              No quick notes yet.
            </p>
          )}

          <AddNoteSection>
            <NoteTextarea
              placeholder="Add a quick note..."
              value={newQuickNote}
              onChange={(e) => setNewQuickNote(e.target.value)}
            />
            <ButtonRow>
              <ElevatedButton
                onClick={handleAddQuickNote}
                disabled={!newQuickNote.trim() || loading}
              >
                Add Quick Note
              </ElevatedButton>
            </ButtonRow>
          </AddNoteSection>
        </Section>

        {/* Todos Section */}
        <Section>
          <SectionTitle>Todos</SectionTitle>
          {notes.todos && notes.todos.length > 0 ? (
            <NotesList>
              {notes.todos.map((todo, index) => (
                <NoteItem key={index}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <input
                      type="checkbox"
                      checked={todo.status === 'COMPLETED'}
                      onChange={() => handleToggleTodoStatus(index)}
                      style={{ margin: 0 }}
                    />
                    <NoteContent style={{
                      textDecoration: todo.status === 'COMPLETED' ? 'line-through' : 'none',
                      opacity: todo.status === 'COMPLETED' ? 0.6 : 1
                    }}>
                      {todo.content}
                    </NoteContent>
                  </div>
                  <NoteTimestamp>
                    {new Date(todo.timestamp).toLocaleString()}
                    <button
                      onClick={() => handleDeleteTodo(index)}
                      style={{
                        marginLeft: '0.5rem',
                        background: 'none',
                        border: 'none',
                        color: '#dc3545',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      Delete
                    </button>
                  </NoteTimestamp>
                </NoteItem>
              ))}
            </NotesList>
          ) : (
            <p style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
              No todos yet.
            </p>
          )}

          <AddNoteSection>
            <NoteTextarea
              placeholder="Add a todo..."
              value={newTodo}
              onChange={(e) => setNewTodo(e.target.value)}
            />
            <ButtonRow>
              <ElevatedButton
                onClick={handleAddTodo}
                disabled={!newTodo.trim() || loading}
              >
                Add Todo
              </ElevatedButton>
            </ButtonRow>
          </AddNoteSection>
        </Section>
      </NotesContainer>
    </Modal>
  );
};

export default CourseNotesModal;